/**
 * Copyright Highway9 Networks Inc.
 */
import type { ComponentType } from "react";
import { Box, type SxProps, type Theme, type SvgIconProps } from "@h9/ui-lib";

/** Inline SVG component (e.g. `DatabaseIcon.tsx`) or Vite-imported `.svg` URL. */
export type SvgIconAssetSource = ComponentType | string;

function isSvgFileSource(source: SvgIconAssetSource): source is string {
  return typeof source === "string";
}

const baseIconSx = (fontSize: SvgIconProps["fontSize"], color: SvgIconProps["color"]) =>
  ({
    display: "inline-flex",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    fontSize: fontSize ?? 24,
    width: "1em",
    height: "1em",
    lineHeight: 0,
    color: color ?? "inherit",
  }) satisfies SxProps<Theme>;

/**
 * Wraps an inline SVG component or imported `.svg` file so it can be used like an MUI SvgIcon.
 *
 * @example
 * createSvgIconAsset(GPUIcon)
 * createSvgIconAsset(gpuIconUrl) // `import gpuIconUrl from "./gpu.svg"`
 */
export function createSvgIconAsset(source: SvgIconAssetSource) {
  return function SvgIconAsset({ sx, style, className, color, fontSize, titleAccess }: SvgIconProps) {
    const boxSx = [
      baseIconSx(fontSize, color),
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ] as SxProps<Theme>;

    if (isSvgFileSource(source)) {
      return (
        <Box component="span" className={className} style={style} title={titleAccess} sx={boxSx}>
          <img
            src={source}
            alt=""
            aria-hidden
            style={{ width: "1em", height: "1em", display: "block", objectFit: "contain" }}
          />
        </Box>
      );
    }

    const Svg = source;
    return (
      <Box component="span" className={className} style={style} title={titleAccess} sx={boxSx}>
        <Svg />
      </Box>
    );
  };
}
